// ============================================
// CERTIFICATES PAGE - i18n
// ============================================

import { createFileRoute } from '@tanstack/react-router';
import { useState, useRef, useMemo } from 'react';
import { Plus, Trash2, Edit2, AlertTriangle, Check, X, Loader2, Award, Upload, FileText, AlertCircle } from 'lucide-react';
import { useMyCertificates, useCertificateTypes, useCreateCertificate, useUpdateCertificate, useDeleteCertificate, useUploadCertificateDocument, useMissingCertificates } from '@/hooks';
import { formatDate, daysUntilExpiry, getCountries, getCountryName } from '@/utils';

// Sertifika kategorileri - getter trap bypass
const CERT_CATEGORIES: Record<string, { tr: string; en: string }> = {
  'ACİL DURUM VE GÜVENLİK': { tr: 'Acil Durum ve Güvenlik', en: 'Emergency & Safety' },
  'TANKER VE YÜK OPERASYONLARI': { tr: 'Tanker ve Yük Operasyonları', en: 'Tanker & Cargo Operations' },
  'YOLCU VE ÖZEL GEMİLER': { tr: 'Yolcu ve Özel Gemiler', en: 'Passenger & Special Vessels' },
  'GÜVERTE OPERASYONEL': { tr: 'Güverte Operasyonel', en: 'Deck Operational' },
  'MAKİNE OPERASYONEL': { tr: 'Makine Operasyonel', en: 'Engine Operational' },
  'OFFSHORE VE ÖZEL ENDÜSTRİ': { tr: 'Offshore ve Özel Endüstri', en: 'Offshore & Special Industry' },
  'YEMEK VE HİJYEN': { tr: 'Yemek ve Hijyen', en: 'Catering & Hygiene' },
};
import * as Dialog from '@radix-ui/react-dialog';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/_authenticated/profile/certificates')({
  component: CertificatesPage,
});

function CertificatesPage() {
  const { t, i18n } = useTranslation(['panel', 'common', 'enums']);
  const countries = useMemo(() => getCountries(i18n.language), [i18n.language]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCert, setEditingCert] = useState<any>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [uploadingCertId, setUploadingCertId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  // Yeni eklenen sertifikanın ID'si — belge yükleme uyarısı için
  const [newlyCreatedCertId, setNewlyCreatedCertId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Zod schema - t() ile çeviri desteği
  const certificateSchema = useMemo(() => z.object({
    certificateTypeId: z.string().min(1, t('certificates.validation.typeRequired')),
    certificateNumber: z.string().min(1, t('certificates.validation.numberRequired')),
    issuingAuthority: z.string().min(1, t('certificates.validation.authorityRequired')),
    issuingCountry: z.string().min(2, t('certificates.validation.countryRequired')),
    issueDate: z.string().min(1, t('certificates.validation.issueDateRequired')),
    expiryDate: z.string().optional(),
    isUnlimited: z.boolean().default(false),
  }), [t]);

  type CertificateFormData = z.infer<typeof certificateSchema>;

  const { data: certsResponse, isLoading } = useMyCertificates();
  const { data: typesResponse } = useCertificateTypes(selectedCategory || undefined);
  const { data: missingCertsResponse } = useMissingCertificates();
  const createMutation = useCreateCertificate();
  const updateMutation = useUpdateCertificate();
  const deleteMutation = useDeleteCertificate();
  const uploadMutation = useUploadCertificateDocument();

  const certificates: any[] = (certsResponse as any)?.data || certsResponse || [];
  const certificateTypes: any[] = (typesResponse as any)?.data || typesResponse || [];
  const missingCertificates: any[] = (missingCertsResponse as any)?.data || missingCertsResponse || [];

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<CertificateFormData>({
    resolver: zodResolver(certificateSchema),
    defaultValues: { isUnlimited: false },
  });

  const isUnlimited = watch('isUnlimited');

  const onSubmit = (data: CertificateFormData) => {
    const formattedData: Record<string, any> = {
      certificateTypeId: data.certificateTypeId,
      certificateNumber: data.certificateNumber,
      issueDate: data.issueDate ? new Date(data.issueDate).toISOString() : undefined,
      issuingAuthority: data.issuingAuthority,
      issuingCountry: data.issuingCountry,
    };
    
    if (!data.isUnlimited && data.expiryDate) {
      formattedData.expiryDate = new Date(data.expiryDate).toISOString();
    }
    
    if (editingCert) {
      updateMutation.mutate(
        { id: editingCert.id, data: formattedData },
        {
          onSuccess: () => {
            setIsModalOpen(false);
            setEditingCert(null);
            reset();
            setSelectedCategory('');
          },
          onError: (error: any) => {
            console.error('Certificate update error:', error);
          },
        }
      );
    } else {
      createMutation.mutate(formattedData, {
        onSuccess: (createdCert: any) => {
          setIsModalOpen(false);
          reset();
          setSelectedCategory('');
          // Yeni eklenen sertifikanın ID'sini kaydet — belge yükleme uyarısı göster
          const certId = createdCert?.id || createdCert?.data?.id;
          if (certId) setNewlyCreatedCertId(certId);
        },
        onError: (error: any) => {
          console.error('Certificate creation error:', error);
        },
      });
    }
  };

  const handleEdit = (cert: any) => {
    setEditingCert(cert);
    if (cert.certificateType?.category) {
      setSelectedCategory(cert.certificateType.category);
    }
    setTimeout(() => {
      reset({
        certificateTypeId: cert.certificateTypeId || '',
        certificateNumber: cert.certificateNumber || '',
        issuingAuthority: cert.issuingAuthority || '',
        issuingCountry: cert.issuingCountry || '',
        issueDate: cert.issueDate ? cert.issueDate.split('T')[0] : '',
        expiryDate: cert.expiryDate ? cert.expiryDate.split('T')[0] : '',
        isUnlimited: !cert.expiryDate,
      });
    }, 0);
    setIsModalOpen(true);
  };

  const handleAddNew = () => {
    setEditingCert(null);
    reset({
      certificateTypeId: '',
      certificateNumber: '',
      issuingAuthority: '',
      issuingCountry: '',
      issueDate: '',
      expiryDate: '',
      isUnlimited: false,
    });
    setIsModalOpen(true);
  };

  const handleDelete = (id: string) => {
    if (confirm(t('certificates.confirm.delete'))) {
      deleteMutation.mutate(id);
    }
  };

  const handleFileUpload = (certId: string) => {
    setUploadingCertId(certId);
    // Yükleme başladığında yeni ekleme uyarısını kapat
    if (newlyCreatedCertId === certId) setNewlyCreatedCertId(null);
    fileInputRef.current?.click();
  };

  const onFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && uploadingCertId) {
      setUploadProgress(0);
      uploadMutation.mutate(
        { id: uploadingCertId, file, onProgress: setUploadProgress },
        {
          onSuccess: () => {
            setUploadingCertId(null);
            setUploadProgress(0);
          },
          onError: () => {
            setUploadingCertId(null);
            setUploadProgress(0);
          },
        }
      );
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getExpiryStatus = (expiryDate: string | null, isUnlimited: boolean) => {
    if (isUnlimited || !expiryDate) return { status: 'valid', label: t('certificates.expiry.unlimited'), color: 'green' };
    const days = daysUntilExpiry(expiryDate);
    if (days < 0) return { status: 'expired', label: t('certificates.expiry.expired'), color: 'red' };
    if (days <= 30) return { status: 'critical', label: t('certificates.expiry.daysLeft', { count: days }), color: 'red' };
    if (days <= 90) return { status: 'warning', label: t('certificates.expiry.daysLeft', { count: days }), color: 'amber' };
    return { status: 'valid', label: formatDate(expiryDate), color: 'green' };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-maritime-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept=".pdf,.jpg,.jpeg,.png"
        onChange={onFileSelected}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('certificates.title')}</h1>
          <p className="text-gray-500 mt-1">{t('certificates.recordCount', { count: certificates.length })}</p>
        </div>
        <button onClick={handleAddNew} className="btn-primary">
          <Plus className="w-4 h-4" />
          {t('certificates.addCertificate')}
        </button>
      </div>

      {/* Missing Certificates Alert */}
      {missingCertificates.length > 0 && (
        <div className="card p-4 border-l-4 border-amber-500 bg-amber-50">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-amber-800">{t('certificates.missing.title')}</h3>
              <p className="text-sm text-amber-700 mt-1">
                {t('certificates.missing.description')}
              </p>
              <div className="mt-3 space-y-2">
                {missingCertificates.slice(0, 3).map((missing: any, index: number) => (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    <span className={`w-2 h-2 rounded-full ${
                      missing.priority === 'high' ? 'bg-red-500' : 
                      missing.priority === 'medium' ? 'bg-amber-500' : 'bg-blue-500'
                    }`} />
                    <span className="text-amber-900 font-medium">{i18n.language === 'en' ? (missing.certificateType?.nameEn || missing.certificateType?.nameTr || missing.certificateType?.name) : (missing.certificateType?.nameTr || missing.certificateType?.name)}</span>
                    <span className="text-amber-700">- {missing.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Yeni Sertifika Eklendi — Belge Yükleme Uyarısı */}
      {newlyCreatedCertId && (() => {
        const newCert = certificates.find((c: any) => c.id === newlyCreatedCertId);
        if (!newCert) return null;
        const isUploading = uploadingCertId === newlyCreatedCertId && uploadMutation.isPending;
        return (
          <div className="card p-4 border-2 border-blue-400 bg-blue-50 animate-pulse-once">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Upload className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900">
                  {t('certificates.uploadReminder.title', { defaultValue: 'Sertifika görselini yükleyin!' })}
                </h3>
                <p className="text-sm text-blue-700 mt-1">
                  {t('certificates.uploadReminder.description', {
                    defaultValue: 'Sertifikanız başarıyla kaydedildi. Profilinizin eksiksiz görünmesi ve şirketlerin sertifikanızı doğrulayabilmesi için lütfen sertifika görselini (PDF veya fotoğraf) yükleyin.',
                  })}
                </p>
                <div className="mt-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <button
                    onClick={() => handleFileUpload(newlyCreatedCertId)}
                    disabled={isUploading}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60 w-full sm:w-auto"
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {uploadProgress > 0 ? `%${uploadProgress}` : t('common:loading', { defaultValue: 'Yükleniyor...' })}
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        {t('certificates.uploadReminder.button', { defaultValue: 'Sertifika Görselini Yükle' })}
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setNewlyCreatedCertId(null)}
                    className="text-sm text-blue-600 hover:text-blue-800 underline text-center sm:text-left py-1"
                  >
                    {t('certificates.uploadReminder.dismiss', { defaultValue: 'Daha sonra yüklerim' })}
                  </button>
                </div>
              </div>
              <button
                onClick={() => setNewlyCreatedCertId(null)}
                className="p-1 text-blue-400 hover:text-blue-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })()}

      {/* Certificates List */}
      {certificates.length === 0 ? (
        <div className="card p-12 text-center">
          <Award className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('certificates.empty.title')}</h3>
          <p className="text-gray-500 mb-4">{t('certificates.empty.description')}</p>
          <button onClick={handleAddNew} className="btn-primary">
            <Plus className="w-4 h-4" />
            {t('certificates.empty.addFirst')}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {certificates.map((cert: any) => {
            const expiry = getExpiryStatus(cert.expiryDate, cert.isUnlimited);
            const isUploading = uploadingCertId === cert.id && uploadMutation.isPending;
            return (
              <div key={cert.id} className="card p-4">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-maritime flex items-center justify-center ${
                    expiry.color === 'red' ? 'bg-red-100' : expiry.color === 'amber' ? 'bg-amber-100' : 'bg-green-100'
                  }`}>
                    {expiry.status === 'expired' ? (
                      <X className={`w-6 h-6 text-red-600`} />
                    ) : expiry.status === 'critical' || expiry.status === 'warning' ? (
                      <AlertTriangle className={`w-6 h-6 ${expiry.color === 'red' ? 'text-red-600' : 'text-amber-600'}`} />
                    ) : (
                      <Check className="w-6 h-6 text-green-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-medium text-gray-900">{i18n.language === 'en' ? (cert.certificateType?.nameEn || cert.certificateType?.nameTr || cert.certificateType?.name) : (cert.certificateType?.nameTr || cert.certificateType?.name) || t('certificates.certificate')}</h3>
                        <p className="text-sm text-gray-500">{cert.certificateNumber}</p>
                      </div>
                      <span className={`badge ${
                        expiry.color === 'red' ? 'badge-error' : expiry.color === 'amber' ? 'badge-warning' : 'badge-success'
                      }`}>
                        {expiry.label}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                      <span>{cert.issuingAuthority}</span>
                      <span>•</span>
                      <span>{getCountryName(cert.issuingCountry)}</span>
                      <span>•</span>
                      <span>{t('certificates.issued')}: {formatDate(cert.issueDate)}</span>
                    </div>
                    {/* Document Status */}
                    {cert.documentUrl ? (
                      <div className="mt-3 flex items-center gap-2">
                        <a
                          href={cert.documentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded hover:bg-green-100 transition-colors"
                        >
                          <FileText className="w-3 h-3" />
                          {t('common:viewDocument')}
                        </a>
                        <button
                          onClick={() => handleFileUpload(cert.id)}
                          disabled={isUploading}
                          className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                        >
                          <Upload className="w-3 h-3" />
                          {t('common:reupload')}
                        </button>
                      </div>
                    ) : (
                      /* Belge yüklenmemiş — belirgin uyarı şeridi */
                      <div className="mt-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                          <span className="text-xs text-amber-800 font-medium">
                            {t('certificates.noDocument', { defaultValue: 'Sertifika görseli henüz yüklenmedi' })}
                          </span>
                        </div>
                        <button
                          onClick={() => handleFileUpload(cert.id)}
                          disabled={isUploading}
                          className="inline-flex items-center justify-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-md transition-colors w-full sm:w-auto"
                        >
                          {isUploading ? (
                            <>
                              <Loader2 className="w-3 h-3 animate-spin" />
                              {uploadProgress > 0 ? `%${uploadProgress}` : t('common:loading', { defaultValue: 'Yükleniyor...' })}
                            </>
                          ) : (
                            <>
                              <Upload className="w-3 h-3" />
                              {t('certificates.uploadDocument', { defaultValue: 'Görseli Yükle' })}
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => handleEdit(cert)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-maritime"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(cert.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-maritime"
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Certificate Modal */}
      <Dialog.Root open={isModalOpen} onOpenChange={(open) => {
        setIsModalOpen(open);
        if (!open) setEditingCert(null);
      }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto z-50">
            <Dialog.Title className="text-lg font-semibold text-gray-900 p-6 border-b border-gray-100">
              {editingCert ? t('certificates.modal.editTitle') : t('certificates.modal.addTitle')}
            </Dialog.Title>
            <Dialog.Description className="sr-only">
              {editingCert ? t('certificates.modal.editDescription') : t('certificates.modal.addDescription')}
            </Dialog.Description>
            
            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
              <div>
                <label className="label">{t('certificates.form.category')}</label>
                <select
                  className="input"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="">{t('common:all')}</option>
                  {Object.entries(CERT_CATEGORIES).map(([key, labels]) => (
                    <option key={key} value={key}>{i18n.language === 'en' ? labels.en : labels.tr}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">{t('certificates.form.type')}</label>
                <select className={`input ${errors.certificateTypeId ? 'input-error' : ''}`} {...register('certificateTypeId')}>
                  <option value="">{t('common:select')}</option>
                  {certificateTypes.map((type: any) => (
                    <option key={type.id} value={type.id}>{i18n.language === 'en' ? (type.nameEn || type.nameTr || type.name) : (type.nameTr || type.nameEn || type.name)}</option>
                  ))}
                </select>
                {errors.certificateTypeId && <p className="mt-1 text-xs text-red-500">{errors.certificateTypeId.message}</p>}
              </div>

              <div>
                <label className="label">{t('certificates.form.number')}</label>
                <input type="text" className={`input ${errors.certificateNumber ? 'input-error' : ''}`} {...register('certificateNumber')} />
                {errors.certificateNumber && <p className="mt-1 text-xs text-red-500">{errors.certificateNumber.message}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('certificates.form.issuingAuthority')}</label>
                  <input type="text" className={`input ${errors.issuingAuthority ? 'input-error' : ''}`} {...register('issuingAuthority')} />
                </div>
                <div>
                  <label className="label">{t('certificates.form.issuingCountry')}</label>
                  <select className={`input ${errors.issuingCountry ? 'input-error' : ''}`} {...register('issuingCountry')}>
                    <option value="">{t('common:select')}</option>
                    {countries.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('certificates.form.issueDate')}</label>
                  <input type="date" className={`input ${errors.issueDate ? 'input-error' : ''}`} {...register('issueDate')} />
                </div>
                <div>
                  <label className="label">{t('certificates.form.expiryDate')}</label>
                  <input type="date" className="input" disabled={isUnlimited} {...register('expiryDate')} />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input type="checkbox" id="isUnlimited" className="w-4 h-4 text-maritime-600 rounded" {...register('isUnlimited')} />
                <label htmlFor="isUnlimited" className="text-sm text-gray-700">{t('certificates.form.unlimited')}</label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <Dialog.Close asChild>
                  <button type="button" className="btn-secondary">{t('common:cancel')}</button>
                </Dialog.Close>
                <button type="submit" className="btn-primary" disabled={createMutation.isPending || updateMutation.isPending}>
                  {(createMutation.isPending || updateMutation.isPending) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {editingCert ? t('common:update') : t('common:add')}
                </button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
